import { useEffect, useRef, ReactElement } from "react";
import { isEmpty } from "ramda";
import { useLocation } from "_/components/router";
import { t } from "@lingui/core/macro";

import { useAddToast } from "_/components/toasts";
import { useUserVerification } from "_/data/users";
import { routeUrls } from "_/routes";

export const EmailVerify = (): ReactElement => {
  const [_location, setLocation] = useLocation();
  const addToast = useAddToast();
  const { mutateAsync: verifyEmail } = useUserVerification();

  // Verification tokens are single-use, so the request must be sent exactly
  // once. StrictMode double-invokes mount effects in development, which
  // would otherwise spend the token twice and report a spurious failure.
  const verificationStarted = useRef(false);

  useEffect(() => {
    if (verificationStarted.current) {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const queryParams = Object.fromEntries(searchParams.entries());

    if (isEmpty(queryParams)) {
      return;
    }

    verificationStarted.current = true;

    // Rewrite history to clear query params on pageload.
    setLocation(routeUrls.index, { replace: true });

    const successTitle = t`components.email-verify.success-title`;
    const successContent = t`components.email-verify.success-content`;

    const errorTitle = t`components.email-verify.error-title`;
    const errorContent = t`components.email-verify.error-content`;

    const verify = async () => {
      try {
        await verifyEmail({
          id: queryParams.i,
          token: queryParams.t,
        });
        setLocation(routeUrls.public.login);
        addToast({
          title: successTitle,
          content: successContent,
        });
      } catch {
        addToast({
          title: errorTitle,
          content: errorContent,
        });
      }
    };

    void verify();
  }, [setLocation, verifyEmail, addToast]);

  return <></>;
};
